import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import {
  getAiTokenBudget,
  resetAiTokenBudget,
  updateAiTokenBudget,
} from '../services/tokenBudgetService';
import type { AiTokenBudget } from '../types/tokenBudget';
import styles from './AiUsage.module.css';

const emptyBudget = (): AiTokenBudget => ({
  maxTokens: 0,
  tokensUsed: 0,
  tokensRemaining: null,
  isExceeded: false,
});

export const AiTokenBudgetPanel: React.FC = () => {
  const { t } = useTranslation('aiUsage');
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManageTokenBudget = hasPermission(PermissionCodes.adminAccess);
  const [form, setForm] = React.useState<AiTokenBudget>(emptyBudget);
  const [loading, setLoading] = React.useState(canManageTokenBudget);
  const [saving, setSaving] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!canManageTokenBudget) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setForm(await getAiTokenBudget());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('failedToLoadTokenBudget'));
    } finally {
      setLoading(false);
    }
  }, [canManageTokenBudget, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      setForm(await updateAiTokenBudget({ maxTokens: form.maxTokens }));
      showToast(t('tokenBudgetSaved'), 'success');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToSaveTokenBudget'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    setError(null);
    try {
      setForm(await resetAiTokenBudget());
      showToast(t('tokenBudgetReset'), 'success');
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : t('failedToResetTokenBudget'));
    } finally {
      setResetting(false);
    }
  };

  if (!canManageTokenBudget) {
    return null;
  }

  return (
    <section className={styles.budget}>
      <div className={styles.budgetHeader}>
        <h4 className={styles.budgetTitle}>{t('tokenBudgetHeading')}</h4>
        <p className={styles.budgetDescription}>{t('tokenBudgetDescription')}</p>
      </div>

      {error ? <div className="error">{error}</div> : null}

      {loading ? (
        <p className={styles.budgetHelp}>{t('loadingTokenBudget')}</p>
      ) : (
        <>
          <label className={styles.budgetField}>
            <span className={styles.budgetLabel}>{t('tokenBudgetLimit')}</span>
            <input
              className={`text-input ${styles.budgetInput}`}
              type="number"
              min={0}
              max={1000000000}
              placeholder={t('tokenBudgetUnlimited')}
              value={form.maxTokens > 0 ? form.maxTokens : ''}
              onChange={event => {
                const raw = event.target.value.trim();
                setForm(current => ({
                  ...current,
                  maxTokens: raw === '' ? 0 : Math.max(0, Math.floor(Number(event.target.value) || 0)),
                }));
              }}
            />
            <span className={styles.budgetHelp}>
              {form.maxTokens > 0
                ? t('tokenBudgetUsage', {
                  used: form.tokensUsed.toLocaleString(),
                  limit: form.maxTokens.toLocaleString(),
                  remaining: (form.tokensRemaining ?? Math.max(0, form.maxTokens - form.tokensUsed)).toLocaleString(),
                })
                : t('tokenBudgetHelp')}
            </span>
          </label>
          <div className={styles.budgetFooter}>
            <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving || resetting}>
              {saving ? t('savingEllipsis', { ns: 'admin' }) : t('saveTokenBudget')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void reset()}
              disabled={saving || resetting || form.maxTokens <= 0}
            >
              {resetting ? t('savingEllipsis', { ns: 'admin' }) : t('resetTokenBudget')}
            </button>
          </div>
        </>
      )}
    </section>
  );
};
